import type { Partner as ApiPartner } from '@/api/partners';
import { QuickCreatePartnerDrawer } from '@/components/partners';
import { Card, CardContent, Input } from '@/components/ui';
import { useCostBearers } from '@/hooks/use-cost-bearers';
import { usePartners } from '@/hooks/use-partners';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Button } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useWizardStore } from '../wizard-store';
import { GrantSelectorSection } from './GrantSelectorSection';

type CostBearerMode = 'self' | 'other';

export function CostBearerStep() {
  const { t } = useTranslation();
  const { partner, costBearerId, setCostBearer } = useWizardStore();
  const { partners: apiPartners, isLoading, refetch } = usePartners();
  // L1(b) #822: a bérlő partner ISMERT költségviselő-poolja (pool-first ajánlás).
  const { costBearers } = useCostBearers(partner?.id);

  // Determine initial mode from store state
  // STORY-1305 AC#4 invariáns: self-mode = costBearerId === partner.id (always-set, payerPartnerId never null)
  const [mode, setMode] = useState<CostBearerMode>(() =>
    costBearerId && costBearerId !== partner?.id ? 'other' : 'self'
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Build cost bearer list: companies and partners with costBearerId, excluding selected partner
  const costBearerOptions = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return apiPartners
      .filter(p => p.id !== partner?.id)
      .filter(p => !searchTerm || p.name.toLowerCase().includes(lowerSearch))
      .map(p => ({
        id: p.id,
        name: p.name,
        type: p.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
      }));
  }, [apiPartners, partner?.id, searchTerm]);

  const handleModeChange = (newMode: CostBearerMode) => {
    setMode(newMode);
    if (newMode === 'self') {
      // STORY-1305 AC#4: self-pay invariáns — payerPartnerId === renterPartnerId, never null
      setCostBearer(partner?.id ?? null);
    } else {
      // Pre-fill with partner's existing cost bearer if available
      setCostBearer(partner?.costBearerId ?? null);
    }
  };

  const handleCostBearerSelect = (id: string) => {
    setCostBearer(id);
  };

  const selectedCostBearerName = useMemo(() => {
    if (!costBearerId) return null;
    // Check if it matches partner's own costBearerName
    if (partner?.costBearerId === costBearerId && partner?.costBearerName) {
      return partner.costBearerName;
    }
    const found = costBearerOptions.find(p => p.id === costBearerId);
    return found?.name ?? null;
  }, [costBearerId, costBearerOptions, partner]);

  if (!partner) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('rental.wizard.costBearerTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-teal-100">
          {t('rental.wizard.costBearerDesc')}
        </p>
      </div>

      {/* Selected partner info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              {partner.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{partner.name}</p>
              <p className="text-sm text-gray-500 dark:text-teal-100">
                {partner.type === 'COMPANY' ? t('partner.company') : t('partner.individual')}
                {partner.city && ` — ${partner.city}`}
              </p>
            </div>
          </div>
          {partner.costBearerName && (
            <div className="mt-3 rounded bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-700 dark:text-blue-300">
              {t('rental.wizard.defaultCostBearer')}{' '}
              <span className="font-medium">{partner.costBearerName}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost bearer selection */}
      <div className="space-y-3">
        {/* Option 1: Partner pays */}
        <label
          className={cn(
            'flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-colors',
            mode === 'self'
              ? 'border-primary bg-[var(--card-bg-selected)]'
              : 'border-gray-200 dark:border-[#2d7d8a] hover:border-gray-300 dark:hover:border-[#2d7d8a]'
          )}
        >
          <input
            type="radio"
            name="cost-bearer-mode"
            value="self"
            checked={mode === 'self'}
            onChange={() => handleModeChange('self')}
            className="h-4 w-4 text-primary focus:ring-primary"
            data-testid="cost-bearer-self"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {t('rental.wizard.partnerPaysSelf')}
            </p>
            <p className="text-sm text-gray-500 dark:text-teal-100">
              {t('rental.wizard.partnerPaysDesc', { name: partner.name })}
            </p>
          </div>
        </label>

        {/* Option 2: Cost bearer pays */}
        <label
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors',
            mode === 'other'
              ? 'border-primary bg-[var(--card-bg-selected)]'
              : 'border-gray-200 dark:border-[#2d7d8a] hover:border-gray-300 dark:hover:border-[#2d7d8a]'
          )}
        >
          <input
            type="radio"
            name="cost-bearer-mode"
            value="other"
            checked={mode === 'other'}
            onChange={() => handleModeChange('other')}
            className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
            data-testid="cost-bearer-other"
          />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-white">
              {t('rental.wizard.costBearerPays')}
            </p>
            <p className="text-sm text-gray-500 dark:text-teal-100">
              {t('rental.wizard.costBearerPaysDesc')}
            </p>
          </div>
        </label>
      </div>

      {/* Cost bearer selector (shown when "other" mode is selected) */}
      {mode === 'other' && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-teal-100">
            {t('rental.wizard.selectCostBearer')}
          </p>

          {/* L1(b) #822: a bérlő ISMERT költségviselő-poolja — pool-first gyors-ajánlás */}
          {costBearers.length > 0 && (
            <div className="space-y-2" data-testid="cost-bearer-pool">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-teal-100">
                {t('rental.wizard.knownCostBearers')}
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {costBearers.map(cb => (
                  <Card
                    key={cb.id}
                    data-testid="cost-bearer-pool-option"
                    className={cn(
                      'cursor-pointer transition-all hover:shadow-md',
                      costBearerId === cb.relatedPartnerId
                        ? 'ring-2 ring-primary shadow-lg bg-primary/5'
                        : ''
                    )}
                    onClick={() => handleCostBearerSelect(cb.relatedPartnerId)}
                  >
                    <CardContent className="p-3">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {cb.relatedPartnerName}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <Input
            placeholder={t('rental.wizard.searchPartner')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="max-w-xs"
          />

          {isLoading && (
            <div className="py-4 text-center">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
              <p className="mt-2 text-sm text-gray-500 dark:text-teal-100">
                {t('rental.wizard.partnersLoading')}
              </p>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {costBearerOptions.map(cb => (
              <Card
                key={cb.id}
                className={cn(
                  'cursor-pointer transition-all hover:shadow-md',
                  costBearerId === cb.id ? 'ring-2 ring-primary shadow-lg' : ''
                )}
                style={
                  costBearerId === cb.id
                    ? {
                        background: 'var(--card-bg-selected)',
                        border: '2px solid hsl(var(--primary))',
                      }
                    : undefined
                }
                onClick={() => handleCostBearerSelect(cb.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {cb.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-teal-100">
                        {cb.type === 'COMPANY' ? t('partner.company') : t('partner.individual')}
                      </p>
                    </div>
                    {costBearerId === cb.id && (
                      <div className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {costBearerOptions.length === 0 && !isLoading && (
            <div className="rounded-lg border-2 border-dashed border-gray-200 dark:border-[#2d7d8a] p-6 text-center">
              <p className="text-sm text-gray-500 dark:text-teal-100">
                {t('rental.wizard.noCostBearerPartner')}
              </p>
            </div>
          )}

          {/* Quick create new partner as cost bearer */}
          <div className="mt-3 rounded-lg border-2 border-dashed border-gray-100 dark:border-gray-800 p-4 text-center">
            <Button variant="link" onClick={() => setDrawerOpen(true)}>
              {t('partner.quickCreate.notFound')}
            </Button>
          </div>

          <QuickCreatePartnerDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            onCreated={(created: ApiPartner) => {
              void refetch();
              handleCostBearerSelect(created.id);
            }}
          />
        </div>
      )}

      {/* Summary */}
      {mode === 'other' && costBearerId && selectedCostBearerName && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            {t('rental.wizard.costBearerLabel')}
            <span className="font-bold">{selectedCostBearerName}</span>
          </p>
        </div>
      )}
      {mode === 'self' && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            {t('rental.wizard.partnerPaysSelfSummary', { name: partner.name })}{' '}
            <span className="font-bold">{partner.name}</span>
          </p>
        </div>
      )}

      {/* STORY-1305 AC#5-#6: pickup authorization grant section */}
      <GrantSelectorSection />
    </div>
  );
}
